import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box,
    CircularProgress, Stack, Typography, Divider, Chip, Grid, Paper
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { formatCurrency } from './PreInterviewFormUtils';
import { notlarToText } from './PreInterviewNotes';
import { buildAppointmentData } from './AppointmentConstants';

const RandevuOlusturModal = ({ isOpen, onClose, preInterviewData, onSuccess }) => {
    const [isSaving, setIsSaving] = useState(false);
    const [randevuBaslangic, setRandevuBaslangic] = useState(null);
    const [randevuBitis, setRandevuBitis] = useState(null);
    const [randevuNotlari, setRandevuNotlari] = useState('');

    useEffect(() => {
        if (isOpen && preInterviewData) {
            if (preInterviewData.teslimTarihi) {
                const teslimDate = preInterviewData.teslimTarihi.toDate
                    ? preInterviewData.teslimTarihi.toDate()
                    : new Date(preInterviewData.teslimTarihi);
                const baslangic = new Date(teslimDate);
                baslangic.setHours(9, 0, 0, 0);
                setRandevuBaslangic(baslangic);
                const bitis = new Date(baslangic);
                bitis.setHours(10, 0, 0, 0);
                setRandevuBitis(bitis);
            } else {
                const tomorrow = new Date();
                tomorrow.setDate(tomorrow.getDate() + 1);
                tomorrow.setHours(9, 0, 0, 0);
                setRandevuBaslangic(tomorrow);
                const bitis = new Date(tomorrow);
                bitis.setHours(10, 0, 0, 0);
                setRandevuBitis(bitis);
            }
            // Ön görüşme notları artık dizi olabilir; randevu notu tek string alan.
            setRandevuNotlari(notlarToText(preInterviewData.notlar));
        }
    }, [isOpen, preInterviewData]);

    const handleSubmit = async () => {
        if (!randevuBaslangic || !randevuBitis) {
            alert('Randevu başlangıç ve bitiş zamanı zorunludur.');
            return;
        }
        if (randevuBitis <= randevuBaslangic) {
            alert('Bitiş zamanı başlangıç zamanından sonra olmalıdır.');
            return;
        }

        setIsSaving(true);
        try {
            // Şema tek üreticiden çıkar. Eski kod `musteriId || ''` yazıyordu:
            // müşteri henüz oluşmadıysa randevu boş string customerId ile kaydoluyor,
            // müşteri linkleri ve eşitlik sorguları bozuluyordu (canlıda 31 kayıt).
            const appointmentData = buildAppointmentData({
                customerName: preInterviewData.adSoyad,
                customerPhone: preInterviewData.telefon,
                customerId: preInterviewData.musteriId,
                serviceType: preInterviewData.ilgilenilenHizmetler?.[0] || 'Diğer',
                eventType: 'Müşteri Araç Teslim Alma',
                notes: randevuNotlari,
                start: randevuBaslangic,
                end: randevuBitis,
                durum: 'Bekleniyor',
                preInterviewId: preInterviewData.id,
                aracId: preInterviewData.aracId || '',
                aracPlaka: preInterviewData.aracPlaka || '',
                aracMarka: preInterviewData.aracMarka || '',
                aracModel: preInterviewData.aracModel || '',
                aracYil: preInterviewData.aracYil || '',
                aracRenk: preInterviewData.aracRenk || '',
                aracTuru: preInterviewData.aracTuru || '',
                toplamTutar: preInterviewData.toplamTutar || 0,
                uygulamalar: preInterviewData.uygulamalar || {},
                ilgilenilenHizmetler: preInterviewData.ilgilenilenHizmetler || [],
                teslimTarihi: preInterviewData.teslimTarihi || null,
                createdAt: serverTimestamp()
            });

            const docRef = await addDoc(collection(db, "appointments"), appointmentData);

            await updateDoc(doc(db, "preInterviews", preInterviewData.id), {
                durum: 'Müşteriye Dönüştü',
                randevuId: docRef.id,
                randevuTarihi: Timestamp.fromDate(randevuBaslangic)
            });

            if (onSuccess) onSuccess(docRef.id);
            onClose();
        } catch (error) {
            console.error("Randevu oluşturulurken hata:", error);
            alert("Randevu oluşturulurken bir hata oluştu.");
            setIsSaving(false); // Sadece hata durumunda state güncelle (başarılıysa unmount olacak)
        }
    };

    if (!preInterviewData) return null;


    return (
        <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">Randevu Oluştur</Typography>
                    <Chip label="Ön Görüşmeden" color="info" size="small" />
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>Müşteri Bilgileri</Typography>
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Ad Soyad:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{preInterviewData.adSoyad}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Telefon:</Typography>
                                    <Typography variant="body2">{preInterviewData.telefon}</Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <Paper variant="outlined" sx={{ p: 2 }}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>Araç Bilgileri</Typography>
                            <Stack spacing={1}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Plaka:</Typography>
                                    <Typography variant="body2" fontWeight="bold">{preInterviewData.aracPlaka || '-'}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Marka/Model:</Typography>
                                    <Typography variant="body2">{preInterviewData.aracMarka} {preInterviewData.aracModel}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2" color="text.secondary">Tip:</Typography>
                                    <Typography variant="body2">{preInterviewData.aracTuru || '-'}</Typography>
                                </Box>
                            </Stack>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider />
                        <Typography variant="subtitle2" color="primary" sx={{ mt: 2 }} gutterBottom>Hizmetler</Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {preInterviewData.ilgilenilenHizmetler?.map((hizmet, index) => (
                                <Chip key={index} label={hizmet} size="small" variant="outlined" />
                            )) || <Typography variant="body2" color="text.secondary">-</Typography>}
                        </Box>
                    </Grid>

                    <Grid item xs={12}>
                        <Paper sx={{ p: 2, backgroundColor: 'grey.50' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="subtitle1" fontWeight="bold">Teklif Toplamı (KDV hariç):</Typography>
                                <Typography variant="h5" color="primary.main" fontWeight="bold">
                                    {formatCurrency(preInterviewData.toplamTutar)}
                                </Typography>
                            </Box>
                        </Paper>
                    </Grid>

                    <Grid item xs={12}>
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="subtitle2" color="primary" gutterBottom>Randevu Zamanı</Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <DateTimePicker
                            label="Başlangıç Zamanı *"
                            value={randevuBaslangic}
                            onChange={setRandevuBaslangic}
                            ampm={false}
                            slotProps={{ textField: { fullWidth: true, required: true } }}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <DateTimePicker
                            label="Bitiş Zamanı *"
                            value={randevuBitis}
                            onChange={setRandevuBitis}
                            ampm={false}
                            slotProps={{ textField: { fullWidth: true, required: true } }}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        <TextField
                            label="Randevu Notları"
                            value={randevuNotlari}
                            onChange={(e) => setRandevuNotlari(e.target.value)}
                            multiline
                            rows={3}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                <Button onClick={onClose} disabled={isSaving}>İptal</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="success"
                    disabled={isSaving}
                >
                    {isSaving ? <CircularProgress size={24} /> : 'Randevu Oluştur'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RandevuOlusturModal;
